import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Card, Row, Col, Form, Input, DatePicker, InputNumber, Select, Button, List, Typography, Flex, Divider, Popconfirm, Space, Tooltip, message, Alert } from "antd";
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, CopyOutlined, ReloadOutlined, EyeOutlined, EditFilled, InfoCircleOutlined, ExportOutlined } from "@ant-design/icons";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useBooking } from "./BookingContext";
import CustomerDetailsModal from "../Customers/CustomerDetailsModal";
import dayjs from "../../dayjs";
import { ulid } from "ulid";
import { Charter, CHARTER_STATUS, QUOTE_STATUS } from "../../model/charter";
import "./BookingDetails.css";
import useAllCustomers from "../Customers/useAllCustomers";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { isEmpty, startCase, last } from "lodash";
import SaveMenu from "../../components/SaveMenu";
import LoadMessage from "../../components/LoadMessage";
import useModel from "../../hooks/useModel";
import { charterModelData, routeModelData, shiftBatModelData } from '../../services/ModelService';
import { DATE_STRING } from "../../model/busRoute";
import useAllStops from "../../hooks/useAllStops";
import useAllRoutes from "../../hooks/useAllRoutes";
import { BusRoute } from "../../model/busRoute";
import { schedule_options } from "../../model/schedule";
import { FilterTags } from "../../containers/FilterTag";
import { filterItems } from "./BookingFilters";
import { createAcronym } from "../Charters/CharterItinerary";
import { ShiftBat } from "../../model/shiftBat";
import { deadrunModelData } from "../../services/ModelService";
import useAllTransfers from "../../hooks/useAllTransfers";
import { ReactComponent as Copy } from '../../assets/icons/Copy.svg';
import { ReactComponent as Trash } from '../../assets/icons/Trash.svg';
import { useAppContext } from "../../libs/contextLib";
import useAllVehicleTypes from "../../hooks/useAllVehicleType";
import ReactDOM from 'react-dom';
import QuoteDetailsModal from '../../components/QuoteDetailsModal';
import CreateScheduleModal from "../../components/CreateScheduleModal";
import useAllCharterSchedules from "../../hooks/useAllCharterSchedules";
import useAllSchedules from "../../hooks/useAllSchedules";
import config from "../../config";
import { secsSinceMidnightToDayjs } from "../../model/timeFilter";
import LoaderButton from "../../components/LoaderButton";
import { ReactComponent as Edit } from '../../assets/icons/Edit.svg';
import useAllCharters from "../Charters/useAllCharters";
import { ReactComponent as Warning } from '../../assets/icons/Warning.svg';
import BookingBilling from './BookingBilling';
import ConfirmationTemplate from "./templates/ConfirmationTemplate";
import InvoiceTemplate from "./templates/InvoiceTemplate";
import ReceiptTemplate from "./templates/ReceiptTemplate";
import QuoteTemplate from "./templates/QuoteTemplate";
import DocumentHistory from "../../components/DocumentHistory";
import { encodeBookingToken } from '../../libs/bookingToken';
const { TextArea } = Input;
const { Paragraph } = Typography;

const BookingDetailsCard = ({ mode, charter, setBookingWith, form, operator }) => {
  const { allCustomers } = useAllCustomers();
  const { allCharterSchedules } = useAllCharterSchedules();
  const { allVehicleTypes } = useAllVehicleTypes();
  const [customers, setCustomers] = useState([]);
  const [newCustomerVisible, setNewCustomerVisible] = useState(false);
  const [createScheduleVisible, setCreateScheduleVisible] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("create");
  const [isOptibusEnabled, setIsOptibusEnabled] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Get customer ID from URL
  const queryParams = new URLSearchParams(location.search);
  const customerIdParam = queryParams.get('customerId');

  useEffect(() => {
    if (operator) {
      setIsOptibusEnabled(!!operator?.opts?.external?.optibus?.apiKey);
    }
  }, [operator]);

  useEffect(() => {
    if (allCustomers) {
      setCustomers(Object.values(allCustomers));

      // Auto-select customer if ID is in URL
      if (customerIdParam && mode.edit && !charter.customerId && (charter.charterId === '_new_' || charter.charterId === '_')) {

        // Look for customer in allCustomers
        const customer = allCustomers[customerIdParam] ||
          Object.values(allCustomers).find(c => c.customerId === customerIdParam);

        if (customer) {

          // Set customer and related fields
          setBookingWith("customerId", customer.customerId);
          setBookingWith("customer", customer);

          // Handle customer address and contact info
          const addressLine1 = customer.addressLine1;
          const addressLine2 = customer.addressLine2;
          const addressContact = customer.phone;
          setBookingWith("address1", addressLine1);
          setBookingWith("address2", addressLine2);
          setBookingWith("addressContact", addressContact);

        }
      }
    }
  }, [allCustomers, mode.edit, charter.charterId, charter.customerId, customerIdParam, setBookingWith]);

  const handleScheduleCreated = (newSchedule) => {
    // Update the charter with the newly created schedule
    setBookingWith("scheduleId", newSchedule.scheduleId);
  };

  const scheduleOptions = useMemo(() => {
    return schedule_options(allCharterSchedules);
  }, [allCharterSchedules]);

  const charterStatusOptions = useMemo(() => {
    const driverDutiesExist = charter.duties.length > 0;
    const isAlreadyScheduled = charter.status === 'scheduled';
    const isCancelled = charter.status === 'cancelled';

    return CHARTER_STATUS.map(status => {
      if (!driverDutiesExist && isOptibusEnabled && status.value === 'scheduled') {
        return {
          ...status,
          label: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ color: '#00000040' }}>{status.label}</span>
            <Tooltip title={<div><p>You need at least one driver duty to schedule a booking</p></div>}>
              <span className="validation-icon"><Warning style={{ height: 14 }} /></span>
            </Tooltip>
          </div>,
          disabled: true
        }
      }
      if (isCancelled && isOptibusEnabled && status.value !== 'cancelled') {
        return {
          ...status,
          label: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ color: '#00000040' }}>{status.label}</span>
            <Tooltip title={<div><p>This booking is cancelled and cannot be modified</p></div>}>
              <span className="validation-icon"><Warning style={{ height: 14 }} /></span>
            </Tooltip>
          </div>,
          disabled: true
        }
      }

      if (isAlreadyScheduled && isOptibusEnabled && status.value !== 'scheduled' && status.value !== 'cancelled' && status.value !== 'completed') {
        return {
          ...status,
          label: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ color: '#00000040' }}>{status.label}</span>
            <Tooltip title={<div><p>This booking is already scheduled and can only be cancelled or completed</p></div>}>
              <span className="validation-icon"><Warning style={{ height: 14 }} /></span>
            </Tooltip>
          </div>,
          disabled: true
        }
      }
      return {
        ...status,
        label: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{status.label}</span>
        </div>
      };
    })
  }, [charter, isOptibusEnabled]);

  const handleCustomerChange = (customer) => {
    if (customer) {
      const addressLine1 = customer.addressLine1;
      const addressLine2 = customer.addressLine2;
      const addressContact = customer.phone;
      setBookingWith("address1", addressLine1);
      setBookingWith("address2", addressLine2);
      setBookingWith("addressContact", addressContact);

      // Always update the discount when customer changes
      // Use the customer's discount if available, otherwise set to null to use system default
      if (customer.discount !== undefined && customer.discount !== null) {
        setBookingWith("discount", customer.discount);
      }
      if (customer.invoicePaymentType !== undefined && customer.invoicePaymentType !== null) {
        setBookingWith("invoicePaymentType", customer.invoicePaymentType);
      }
    }
  }

  useEffect(() => {
    charter.generateQuote({
      operator,
      allVehicleTypes,
      allCharterSchedules
    });
  }, [charter.discount, charter.marginInclusion])

  const hasIntegrationError = useMemo(() => {
    let error = null;
    if (charter?.integration?.app === 'optibus' && charter?.integration?.status === 'failed') {
      error = charter?.integration?.message || "Something went wrong with optibus integration";
    }
    // testing (uncomment to test)
    // error = "Something went wrong with optibus integration";

    return error;
  }, [charter])

  return (
    <Card title="Booking Details" className="card-main card-info row-separator">
      {hasIntegrationError && (
        <Alert message={hasIntegrationError} type="error" />
      )}
      <Row gutter={[30, 30]}>
        <Col xs={24} sm={12}>
          <label>Booking Summary</label>
          {mode.edit ? (
            <Input
              placeholder="Enter booking summary"
              // status={!charter.name?.length ? "error" : ""}
              value={charter.name}
              onChange={({ target: { value } }) => setBookingWith("name", value)}
            />
          ) : (
            <strong>{charter.name}</strong>
          )}
        </Col>
        <Col xs={24} sm={12}>
          <label>Booking Reference</label>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <strong>{charter.bookingReference}</strong>
            {charter.bookingReference && (
              <Button
                className="icon-button"
                title="Copy booking reference"
                size="sm"
                variant="light"
                style={{ padding: '2px', minWidth: '24px', height: '24px' }}
                onClick={() => {
                  navigator.clipboard.writeText(charter.bookingReference).then(() => {
                    message.info("Booking reference copied to clipboard");
                  });
                }}
              >
                <CopyOutlined />
              </Button>
            )}
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <label>Booking Type</label>
          {mode.edit ? (
            <Select
              value={charter.charterType}
              options={[
                {
                  value: "standard",
                  label: "Standard booking",
                },
                { value: "recurring", label: "Contract booking" },
              ]}
              onChange={(value) => {
                setBookingWith("charterType", value)
              }}
            />
          ) : (
            <strong>
              {charter.charterType === "standard"
                ? "Standard booking"
                : charter.charterType === "recurring"
                  ? "Contract booking"
                  : "TBC"}
            </strong>
          )}
        </Col>
        {charter.charterType === "standard" &&
          <Col xs={24} sm={12}>
            <label>Travel dates</label>
            {mode.edit ? (
              <DatePicker
                multiple
                placeholder="Select travel dates"
                {...(!isEmpty(charter.dates) ? { value: charter.dates } : { value: [] })}
                status={!charter.dates?.length ? "error" : ""}
                format={{
                  format: DATE_STRING,
                  type: "mask",
                }}
                onChange={(value) => {
                  setBookingWith("dates", value);
                }}
              />
            ) : (
              <strong>
                {charter.dates?.map((date, index) =>
                  date?.format ? date.format(DATE_STRING) : ' '
                ).join(", ")}
              </strong>
            )}
          </Col>
        }
        {charter.charterType === "recurring" &&
          <Col xs={24} sm={12}>
            <label>Travel Schedule</label>
            {mode.edit ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Select
                    placeholder="Travel schedule"
                    showSearch={true}
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    filterSort={(optionA, optionB) => (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())}
                    onChange={(scheduleId) => {
                      setBookingWith("scheduleId", scheduleId);
                    }}
                    value={charter.scheduleId}
                    allowClear
                    style={{
                      width: 'calc(100% - 76px)',
                    }}
                    options={scheduleOptions}
                  />
                  <div style={{ display: "flex", gap: 4 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setCreateScheduleVisible(true)
                        setScheduleMode("create")
                      }}
                      title="Create new travel schedule"
                      className="icon-button btn-sm"
                      style={{ minHeight: "32px" }}
                    />
                    {charter.scheduleId &&
                    <Button
                      type="primary"
                        icon={<Edit style={{ width: 14 }} />}
                      onClick={() => {
                        setCreateScheduleVisible(true)
                        setScheduleMode("edit")
                      }}
                        title="Edit travel schedule"
                      className="icon-button btn-sm btn-hover-light"
                      style={{ minHeight: "32px" }}
                      />}
                  </div>
                </div>
                <CreateScheduleModal
                  visible={createScheduleVisible}
                  onClose={() => setCreateScheduleVisible(false)}
                  onScheduleCreated={handleScheduleCreated}
                  scheduleId={scheduleMode === "create" ? null : charter.scheduleId}
                />
              </>
            ) : (
              <strong>
                {(charter.scheduleId && allCharterSchedules ? allCharterSchedules[charter.scheduleId]?.scheduleName : "NA")}
              </strong>
            )}
          </Col>
        }
        <Col xs={24} sm={12}>
          <label>Customer</label>
          {mode.edit ? (
            <>
              <CustomerDetailsModal
                visible={newCustomerVisible}
                closeDisplay={() => setNewCustomerVisible(false)}
                onNewCustomer={(customer) => {
                  setCustomers((customers) => [...customers, customer]);
                  setBookingWith(["customerId", "customer"], [customer.customerId, customer]);
                  setNewCustomerVisible(false);
                  handleCustomerChange(customer);
                }}
              />
              <Select
                style={{ width: "100%" }}
                allowClear
                placeholder="Select a customer"
                onChange={(value) => {
                  const customer = customers.find((c) => c.customerId === value);
                  setBookingWith(["customerId", "customer"], [value, customer]);
                  handleCustomerChange(customer);
                }}
                value={charter.customerId}
                showSearch
                filterOption={(input, option) => {
                  if (!option || !option.label) return false;
                  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                status={!charter.customerId ? "error" : ""}
                options={customers.map((customer) => ({
                  label: customer.name,
                  value: customer.customerId,
                }))}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    <Divider style={{ margin: "4px 0" }} />
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "nowrap",
                        padding: 8,
                      }}
                    >
                      <Button
                        className="btn-secondary"
                        style={{
                          color: "#1890ff",
                          flex: "none",
                          padding: "0 8px",
                          display: "block",
                          cursor: "pointer",
                        }}
                        onClick={() => setNewCustomerVisible(true)}
                      >
                        <PlusOutlined style={{ height: 12 }} className="mr-1" /> Customer
                      </Button>
                    </div>
                  </div>
                )}
              />
            </>
          ) : (
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <strong>{charter.customer?.name || "NA"}</strong>
              {charter.customer?.customerId && (
                <Button
                  className="icon-button"
                  title="View customer details"
                  size="sm"
                  variant="light"
                  style={{ padding: '2px', minWidth: '24px', height: '24px' }}
                  onClick={() => history.push(`/customers/${charter.customer.customerId}`)}
                >
                  <EyeOutlined />
                </Button>
              )}
            </div>
          )}
        </Col>
        <Col xs={24} lg={12}>
          <label>Discount</label>
          {mode.edit ? (
            <InputNumber
              min={0}
              max={100}
              placeholder="Enter Discount (%) or leave blank for default"
              defaultValue={charter.discount}
              value={charter.discount}
              onChange={(value) => setBookingWith("discount", value)}
              className="w-100"
            />
          ) : (
            <strong>{charter.discount ? `${charter.discount}%` : '0%'}</strong>
          )}
        </Col>
        <Col xs={24} lg={12}>
          <label>Invoice Payment Type</label>
          {mode.edit ? (
            <Select
              placeholder="Select Invoice Payment Type"
              value={charter.invoicePaymentType}
              onChange={(value) => setBookingWith("invoicePaymentType", value)}
            >
              <Select.Option value="in-advance">In-Advance</Select.Option>
              <Select.Option value="in-arrears">In-Arrears</Select.Option>
            </Select>
          ) : (
            <strong>{charter.invoicePaymentType ? charter.invoicePaymentType === 'in-arrears' ? 'In-Arrears' : 'In-Advance' : '-'}</strong>
          )}
        </Col>
        <Col xs={24} lg={12}>
          <label>Booking Margin</label>
          {mode.edit ? (
            <InputNumber
              min={0}
              max={100}
              placeholder="Enter Margin Inclusion (%) or leave blank for default"
              defaultValue={charter.marginInclusion || charter.getMarginInclusion(operator)}
              value={charter.marginInclusion}
              onChange={(value) => setBookingWith("marginInclusion", value)}
              className="w-100"
            />
          ) : (
            <strong>{charter.getMarginInclusion(operator) !== null ? `${charter.getMarginInclusion(operator)}%` : '0%'}</strong>
          )}
        </Col>
        <Col xs={24} sm={12}>
          <label>Passengers</label>
          {mode.edit ? (
            <InputNumber
              placeholder="Enter number of passengers"
              value={charter.passengerCount}
              onChange={(value) => setBookingWith("passengerCount", value)}
              className="w-100"
            />
          ) : (
            <strong>{charter.passengerCount}</strong>
          )}
        </Col>
        <Col xs={24} sm={12}>
          <label>Booking Status</label>
          {mode.edit ? (
            <Select
              value={charter.status}
              status={!charter.status ? "error" : ""}
              options={charterStatusOptions}
              onChange={(value) => setBookingWith("status", value)}
              defaultValue={"new"}
            />
          ) : (
            <div className="simple-tags">
              <FilterTags filterItems={filterItems} model={charter} />
            </div>
          )}
        </Col>
        <Col xs={24} sm={12}>
          <label>Additional booking details</label>
          {mode.edit ? (
            <TextArea
              placeholder="Enter additional booking details"
              value={charter.note}
              onChange={({ target: { value } }) => setBookingWith("note", value)}
              rows={4}
            />
          ) : (
            <strong>
              {(charter.note || "").split("\n").map((p, index) => (
                <Paragraph key={`note-paragraph-${index}`}>{p}</Paragraph>
              ))}
            </strong>
          )}
        </Col>
      </Row>
    </Card>
  );
};

const CharterLegsCard = ({ mode, setMode, charter, setBookingWith, setCharter }) => {
  const { allStops, setAllStops } = useAllStops();
  const { allRoutes } = useAllRoutes();
  const { allCharterSchedules } = useAllCharterSchedules();
  const history = useHistory();

  // Calculate depot times based on route data
  const getStartDepotDepartureTime = (route) => {
    if (!route || !route.startDepot || !route.stops || route.stops.length === 0 || !charter.dates || !charter.dates[0]) {
      return null;
    }

    const firstStop = route.stops[0];
    if (!firstStop.arriveSecs) return null;

    // Subtract the travel duration from the first stop's arrival time
    const departureSecsFromFirstStop = firstStop.arriveSecs - (route.startDepotDuration || 0);

    // Create a dayjs object with the calculated time
    const baseDate = dayjs(charter.dates[0]);
    const secsInDay = 24 * 60 * 60;
    const daysDiff = Math.floor(departureSecsFromFirstStop / secsInDay);

    return baseDate.clone().startOf('day')
      .add(daysDiff, 'days')
      .add(departureSecsFromFirstStop % secsInDay, 'seconds');
  };

  const getEndDepotArrivalTime = (route) => {
    if (!route || !route.endDepot || !route.stops || route.stops.length === 0 || !charter.dates || !charter.dates[0]) {
      return null;
    }

    const lastStop = last(route.stops);
    if (!lastStop.departSecs) return null;

    // Add the travel duration to the last stop's departure time
    const arrivalSecsFromLastStop = lastStop.departSecs + (route.endDepotDuration || 0);

    // Create a dayjs object with the calculated time
    const baseDate = dayjs(charter.dates[0]);
    const secsInDay = 24 * 60 * 60;
    const daysDiff = Math.floor(arrivalSecsFromLastStop / secsInDay);

    return baseDate.clone().startOf('day')
      .add(daysDiff, 'days')
      .add(arrivalSecsFromLastStop % secsInDay, 'seconds');
  };

  // Calculate actual duration between start and end depots
  const getActualTripDuration = (route) => {
    const totalSeconds = route?.getTotalDuration() || 0;

    if (totalSeconds > 0) {
      const days = Math.floor(totalSeconds / 86400); // 86400 = 24 * 60 * 60
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.ceil((totalSeconds % 3600) / 60);

      return { days, hours, minutes };
    }

    const startTime = getStartDepotDepartureTime(route);
    const endTime = getEndDepotArrivalTime(route);

    if (!startTime || !endTime) return { days: 0, hours: 0, minutes: 0 };

    // Calculate duration in seconds
    const durationInSeconds = endTime?.diff(startTime, 'second') || 0;

    const days = Math.floor(durationInSeconds / 86400); // 86400 = 24 * 60 * 60
    const hours = Math.floor((durationInSeconds % 86400) / 3600);
    const minutes = Math.ceil((durationInSeconds % 3600) / 60);

    return { days, hours, minutes };
  };

  const newRoute = (charter, legNum = 1) => {
    const route = new BusRoute({
      routeId: ulid(),
      routeNumber: createAcronym(charter.name) + '-' + legNum,
      routeName: `Charter ${legNum}`,
      routeDetails: charter.name,
      published: 1,
      charter: true,
      stops: [],
      distance: 0,
      duration: 0,
      startTime: charter.startTime || 0,
      endTime: charter.endTime || 0,
      scheduleId: charter.scheduleId,
      vehicleTypes: charter.vehicleTypes || [],
      driverType: charter.driverType,
      passengerCount: charter.passengerCount,
      status: 'active'
    });
    return route;
  };

  const handleAddLeg = async () => {
    let route = newRoute(charter, charter.itinerary.length + 1);
    await routeModelData.save(route);
    const _charter = charter.clone();
    _charter.itinerary = _charter.itinerary.concat(route);
    setCharter(_charter);
    const savedCharter = await charterModelData.save(_charter);
    setMode({ edit: false });
    history.push(`/bookings/${savedCharter.charterId}/charter/${route.routeId}?edit=true`);
  };

  const handleRemoveLeg = (routeId) => {
    setBookingWith(
      "itinerary",
      charter.itinerary.filter((route) => route.routeId !== routeId)
    );
  };

  const handleCopyLeg = async (routeId) => {
    const routeToCopy = charter.itinerary.find((route) => route.routeId === routeId);
    if (!routeToCopy) return;

    const newRoute = routeToCopy.clone();
    newRoute.routeId = ulid();

    const _charter = charter.clone();
    _charter.itinerary = _charter.itinerary.concat(newRoute);

    await charterModelData.save(_charter);
    setBookingWith("itinerary", _charter.itinerary);
  };

  const handleLegClick = async (route) => {
    history.push(`/bookings/${charter.charterId}/charter/${route.routeId}?${mode.edit ? 'edit=true' : ''}`);
    // window.open(`/bookings/${charter.charterId}/charter/${route.routeId}`, '_self');
  };

  const validateRoute = (route) => {
    const validations = route.validate()
    const messages = []
    validations.stops.forEach(s => messages.push(...s))

    // check if any of the trip dates are missing from the charter dates
    const charterDates = charter.dates?.map(d => dayjs(d, DATE_STRING)) || []
    const tripDates = []
    route.services.forEach(t => {
      if(t.date) {
        tripDates.push(dayjs(t.date, DATE_STRING))
      }
      (t.dates || []).forEach(d => tripDates.push(dayjs(d, DATE_STRING)))
    })
    // Find trip dates that are not in charter dates
    const invalidTripDates = tripDates.filter(td => !charterDates.some(cd => cd.isSame(td, 'day')))
    if (invalidTripDates.length > 0 && charter.charterType !== 'recurring') {
      messages.push({ field: 'dates', msg: `Some trips are using dates that are not part of the charter's travel dates` })
    }
    return messages
  }

  return (
    <Card title="Charters" bordered={false} className="card-main booking-details">
      {mode.edit && charter.status !== 'cancelled' && charter.status !== 'scheduled' && (
        <Button type="primary" className="icon-button btn-sm card-btn" onClick={handleAddLeg} icon={<PlusOutlined />}>
          Add Charter
        </Button>
      )}
      <List
        dataSource={charter.itinerary || []}
        renderItem={(route) => {
          const messages = validateRoute(route)

          return (
            <List.Item
              onClick={() => handleLegClick(route)}
              style={{ cursor: "pointer" }}
              actions={
                mode.edit
                  ? [
                    <Button
                      size="small"
                      className="btn-icon-control"
                      icon={<Copy style={{ width: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyLeg(route.routeId);
                      }}
                    >
                    </Button>,
                    <Button
                      size="small"
                      className="btn-icon-control btn-delete"
                      icon={<Trash style={{ width: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLeg(route.routeId);
                      }}
                    >
                    </Button>,
                  ]
                  : null
              }
            >
              
              <List.Item.Meta
                title={route.stops && route.stops.length > 0 && route.stops[0]?.stopName && route.stops[route.stops.length - 1]?.stopName ?
                  (() => {
                    // const charterMatch = route.routeName.match(/Charter\s+\d+/);
                    // const charterPrefix = charterMatch ? charterMatch[0] : "";
                    // return `${charterPrefix}${charterPrefix ? " - " : ""}${route.stops[0].stopName} → ${route.stops[route.stops.length - 1].stopName}`;
                    return `${route.routeName}${route.routeName ? " - " : ""}${route.stops[0].stopName} → ${route.stops[route.stops.length - 1].stopName}`;
                  })() :
                  route.routeName}
                description={`${route.stops?.length || 0} stops, ${route.getTotalDistance() ? (route.getTotalDistance() / 1000).toFixed(1) + "km" : "No distance"}${route.getTotalDuration() ? (() => {
                  const duration = getActualTripDuration(route);
                  let durationString = '';
                  if (duration.days > 0) {
                    durationString += `${duration.days}d `;
                  }
                  return `, ${durationString}${duration.hours}hr ${duration.minutes}m, ${route.services?.length || 0} ${route.services?.length > 1 ? 'trips' : 'trip'}`;
                })() : ""
                  }`}
              />
             {
              messages.length> 0 &&  
              <Tooltip title={<div>{messages.map(m => (
                <>
                  <span key={m.field}>{m.msg}</span> < br />
                </>
              ))}</div>}>
                <span className="validation-icon"><Warning style={{ height: 14 }} /></span>
              </Tooltip>
             }
            </List.Item>
          )
        }}
      />
    </Card>
  );
};

const ShiftbatCard = ({ mode, setMode, charter, setBookingWith, setCharter }) => {
  const { allStops, setAllStops } = useAllStops();
  const { allRoutes } = useAllRoutes();
  const { allCharterSchedules } = useAllCharterSchedules();
  const { allTransfers } = useAllTransfers();
  const history = useHistory();

  const [isGeneratingShiftbat, setIsGeneratingShiftbat] = useState(false);

  const handleShiftbatClick = (shiftbat) => {
    // window.open(`/bookings/${charter.charterId}/duties/${shiftbat.shiftBatId}`, '_blank');
    history.push(`/bookings/${charter.charterId}/duties/${shiftbat.shiftBatId}?${mode.edit ? 'edit=true' : ''}`);
  };

  const handleDeleteShiftbat = async (shiftbatId) => {
    const newDuties = charter.duties.filter(duty => duty.shiftBatId !== shiftbatId);
    await shiftBatModelData.delete(shiftbatId);
    setBookingWith("duties", newDuties);
  };

  const handleAutogenerateShiftbat = async () => {
    try {
      setIsGeneratingShiftbat(true);
      // const shiftbat = await ShiftBat.fromCharterItinerary({
      //   charter: {
      //     ...charter,
      //     itinerary: charter.itinerary,
      //   },
      //   depot: charter.express?.startDepot || null,
      //   endDepot: charter.express?.endDepot || null,
      //   deadrunModelData,
      //   allStops,
      //   allTransfers,
      //   allRoutes,
      // });

      const shiftbat = new ShiftBat({
        shiftBatId: ulid(),
        shiftBatName: `Driver Duty ${charter.duties.length + 1}`,
        rows: [],
        status: 'active',
        effectiveDate: charter.dates?.[0] || charter.startDate,
        charter: true,
      });

      shiftbat.effectiveDate = charter.dates?.[0] || charter.startDate;
      shiftbat.dates = charter.dates;
      shiftbat.scheduleIds = charter.scheduleId ? [charter.scheduleId] : [];
      shiftbat.vehicleType = charter.vehicleTypes?.[0] || null;

      await shiftBatModelData.save(shiftbat);

      const newDuties = [...(charter.duties || []), shiftbat];
      setBookingWith("duties", newDuties);

      const _charter = charter.clone();
      _charter.duties = newDuties;
      setCharter(_charter);
      await charterModelData.save(_charter);
      setIsGeneratingShiftbat(false);
      setMode({ edit: false });
      history.push(`/bookings/${charter.charterId}/duties/${shiftbat.shiftBatId}?edit=true`);
    } catch (error) {
      console.error("Failed to autogenerate shiftbat:", error);
      setIsGeneratingShiftbat(false);
    }
  };

  return (
    <Card title="Driver Duties" bordered={false} className="card-main booking-details">
      {charter.status !== 'cancelled' &&
      <LoaderButton isLoading={isGeneratingShiftbat} type="primary" className="icon-button btn-sm card-btn" onClick={handleAutogenerateShiftbat} icon={<PlusOutlined />} style={{ gap: 5 }}>
        Add Driver Duty
      </LoaderButton>
      }
      <List
        dataSource={charter.duties || []}
        renderItem={(shiftbat) => (
          <List.Item
            onClick={() => handleShiftbatClick(shiftbat)}
            style={{ cursor: "pointer" }}
            actions={mode.edit ? [
              <Popconfirm
                title="Are you sure you want to delete this driver duty?"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleDeleteShiftbat(shiftbat.shiftBatId)
                }}
                onCancel={e => {
                  e.stopPropagation();
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="small"
                  className="btn-icon-control btn-delete"
                  icon={<Trash style={{ width: 14 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                </Button>
              </Popconfirm>
            ] : []}
          >
            <List.Item.Meta
              title={shiftbat.shiftBatName}
              description={`${shiftbat.rows?.length || 0} rows, ${shiftbat.getShiftDistance() ? (shiftbat.getShiftDistance() / 1000).toFixed(1) + "km" : "No distance"
                }, Dates: ${shiftbat.dates?.map(date => dayjs(date).format("DD/MM/YYYY")).join(", ")}`}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

const HistoryCard = ({ mode, charter, setBookingWith }) => {
  const [historyLog, setHistoryLog] = useState(charter.historyLog || []);

  const handleStatusChange = (newStatus) => {
    const oldStatus = charter.status;
    const newHistory = [
      ...historyLog,
      {
        id: ulid(),
        timestamp: new Date(),
        action: "Status Change",
        details: `Status changed from ${oldStatus} to ${newStatus}`,
      },
    ];
    setHistoryLog(newHistory);
    setBookingWith("historyLog", newHistory);
  };

  return (
    <Card title="History" bordered={false} className="card-main booking-details">
      <List
        dataSource={historyLog}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={item.action} description={`${dayjs(item.timestamp).format("YYYY-MM-DD HH:mm:ss")} - ${item.details}`} />
          </List.Item>
        )}
      />
    </Card>
  );
};

function BookingDetails() {
  const [form] = Form.useForm();
  const { operator, user, apiKey } = useAppContext();
  const { allCharterSchedules } = useAllCharterSchedules();
  const { allVehicleTypes } = useAllVehicleTypes();
  const { allSchedules } = useAllSchedules();
  const { allChartersArray } = useAllCharters();
  const { allCustomers } = useAllCustomers();
  const location = useLocation();
  const history = useHistory();
  const [pdfType, setPdfType] = useState('quote');
  const {charterId} = useParams();

  // Get date from query parameter
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get('date');
  const customerIdParam = queryParams.get('customerId');
  const editFromQuery = queryParams.get('edit') === 'true';
  const initialDate = dateParam ? dayjs(dateParam).startOf('day') : dayjs().startOf('day');
  

  const {
    model: charter,
    setModel: setCharter,
    initialModelRef: initialBookingRef,
    mode,
    setMode,
    controls,
  } = useModel({
    modelService: charterModelData,
    initialInstance: new Charter({
      dates: [initialDate.format(DATE_STRING)],
    }),
    edit: charter => charter.charterId === '_new_' || charter.charterId === '_', // Auto-enable edit mode for new bookings
  });

  // Set edit mode from query parameter if specified
  useEffect(() => {
    // If edit is explicitly requested via URL, set to edit mode
    if (editFromQuery) {
      setMode({ edit: true });
    }
    // If it's an existing charter (not a new one), force to view mode
    // unless edit was explicitly requested in the URL
    else if (charter.charterId !== '_new_' && charter.charterId !== '_') {
      setMode({ edit: false });
    }
  }, [editFromQuery, charter.charterId, setMode]);

  // Set the charter type to standard when creating a new booking with a date
  useEffect(() => {
    if (dateParam && (charter.charterId === '_new_' || charter.charterId === '_')) {
      setCharter((prev) => {
        const newCharter = new Charter({ ...prev });
        newCharter.charterType = 'standard';
        return newCharter;
      });
    }
  }, [dateParam, charter.charterId, setCharter]);

  // Set the customer when creating a new booking with a customerId
  // This useEffect will run when allCustomers is loaded
  useEffect(() => {
    if (customerIdParam && (charter.charterId === '_new_' || charter.charterId === '_') && allCustomers) {
      // First try direct lookup
      let customer = allCustomers[customerIdParam];

      // If that doesn't work, try finding by ID
      if (!customer) {
        const customerValues = Object.values(allCustomers);
        customer = customerValues.find(c => c.customerId === customerIdParam);
      }

      if (customer) {
        // Update the charter with the customer
        setCharter(prevCharter => {
          // Only update if not already set to avoid infinite loop
          if (prevCharter.customerId !== customer.customerId) {
            const newCharter = new Charter({ ...prevCharter });
            newCharter.customerId = customer.customerId;
            newCharter.customer = customer;

            // Also set related customer fields
            if (customer.addressLine1) newCharter.address1 = customer.addressLine1;
            if (customer.addressLine2) newCharter.address2 = customer.addressLine2;
            if (customer.phone) newCharter.addressContact = customer.phone;

            return newCharter;
          }
          return prevCharter;
        });
      }
    }
  }, [customerIdParam, charter.charterId, allCustomers, setCharter]);

  const contentRef = useRef();
  const [mailData, setMailData] = useState({
    to: [],
    attachments: [],
    subject: `Your Charter Quote – ${operator?.accreditationName}`,
    template: `${operator?.companyId}-Invoice: Charter`,
  });

  const [confirmationToken, setConfirmationToken] = useState(null);

  useEffect(() => {
    async function generateToken() {
      if (!charter) return;
      const payload = {
        charterId: charter.charterId,
        userId: operator.operatorId,
        exp: Math.floor(Date.now() / 1000) + (60 * 60 * 24 * 7) // 7 days expiry
      };
      if(payload.charterId !== '_new_' && payload.charterId !== '_') {
        const token = await encodeBookingToken(payload);
        setConfirmationToken(token);
      }
    }
    generateToken().then(() => {
      console.log('confirmationToken generated');
    });
  }, [charter.charterId, charter.userId, operator, apiKey, charter]);

  useEffect(() => {
    if (operator) {
      const template = {
        quote: `${operator.companyId}-Quote: Charter`,
        invoice: `${operator.companyId}-Invoice: Charter`,
        receipt: `${operator.companyId}-Receipt: Charter`,
      }
      setMailData((mail) => ({
        ...mail,
        subject: `Your Charter Quote ${operator.accreditationName ? `– ${operator.accreditationName}` : ''}`,
        template: template[pdfType],
        merge: {
          ...mail.merge,
          companyName: operator.accreditationName,
          companyPhone: operator.operatorPhone,
          companyEmail: operator.operatorEmail,
          companyWebsite: operator.operatorUrl,
        }
      }));
    }

    if (user) {
      setMailData((mail) => ({
        ...mail,
        merge: {
          ...mail.merge,
          bookingOfficerName: user.attributes.given_name + ' ' + user.attributes.family_name,
        }
      }))
    }

    if (charter) {
      const firstStops = charter.itinerary[0]?.stops || [];
      const lastStops = charter.itinerary[charter.itinerary.length - 1]?.stops || [];
      const vehicleTypes = []
      charter.itinerary.forEach(route => {
        route.vehicleTypes.forEach(vehicleType => {
          if (!vehicleTypes.includes(allVehicleTypes?.[vehicleType].vehicleTypeName)) {
            vehicleTypes.push(allVehicleTypes?.[vehicleType].vehicleTypeName);
          }
        });
      });

      // Generate confirmation token and link
      let confirmationLink = `${window.location.origin}/confirm-booking?token=${encodeURIComponent(confirmationToken)}`;
      setMailData((mail) => ({
        ...mail,
        merge: {
          ...mail.merge,
          customerFirstName: charter.customer?.name?.split(' ')[0] || 'Customer',
          pickupLocation: firstStops.length > 0 ? firstStops[0].stopName : 'TBC',
          dropOffLocation: lastStops.length > 0 ? lastStops[lastStops.length - 1]?.stopName : 'TBC',
          travelDates: charter.dates?.map(date => dayjs(date).format('DD/MM/YYYY')).join(', '),
          noOfPassengers: charter.passengerCount || 'TBC',
          vehicleTypes: vehicleTypes.join(', ') || 'TBC',
          acceptQuoteLink: `${window.location.origin}/bookings/${charter.charterId}?acceptQuote=true`,
          confirmationLink,
        }
      }))
    }
  }, [operator, user, charter, allVehicleTypes, pdfType, confirmationToken]);

  const setBookingWith = useCallback(
    async (propName, value) => {
      if (!Array.isArray(propName)) {
        propName = [propName];
        value = [value];
      }
      setCharter((charter) => {
        const newBooking = new Charter({ ...charter });
        propName.forEach((prop, idx) => {
          if (value[idx] === undefined || value[idx] === null) {
            delete newBooking[prop];
            return;
          }
          newBooking[prop] = value[idx];
        });
        return newBooking;
      });
    },
    [setCharter]
  );

  useEffect(() => {
    if (!charter.bookingReference && allChartersArray !== null) {
      setBookingWith("bookingReference", charter.generateBookingReference(allChartersArray));
    }

    // Set default quote date if it doesn't exist
    if (!charter.quoteDate && (charter.charterId === '_new_' || charter.charterId === '_')) {
      const today = dayjs();
      setBookingWith(["quoteDate", "paymentDue"], [today, today.add(2, 'w')]);
    }
  }, [charter, setBookingWith, allChartersArray]);

  // Update booking address when customer changes
  useEffect(() => {
    // Only update existing bookings (not new ones) that have a customer
    if (charter.customerId && charter.charterId !== '_new_' && charter.charterId !== '_' && charter.customer) {
      // Compare customer address data with booking address data
      const customerAddressLine1 = charter.customer.addressLine1;
      const customerAddressLine2 = charter.customer.addressLine2;
      const customerPhone = charter.customer.phone;
      const customerDiscount = charter.customer.discount;

      // Check if the address data needs to be updated
      const addressNeedsUpdate =
        (customerAddressLine1 && charter.address1 !== customerAddressLine1) ||
        (customerAddressLine2 && charter.address2 !== customerAddressLine2) ||
        (customerPhone && charter.addressContact !== customerPhone);

      // Always check if discount needs to be updated, regardless of whether customer has a specific discount
      const discountNeedsUpdate = charter.discount !== customerDiscount;

      // Update address data if needed
      if (addressNeedsUpdate) {
        setBookingWith([
          "address1",
          "address2",
          "addressContact"
        ], [
          customerAddressLine1 || charter.address1,
          customerAddressLine2 || charter.address2,
          customerPhone || charter.addressContact
        ]);
      }
    }
  }, [charter.customer, charter.customerId, charter.charterId, setBookingWith]);

  const handleSave = async () => {
    try {
      const formData = await form.validateFields();
      const startDate = formData.startDate || charter.startDate;
      const endDate = formData.endDate || charter.endDate;
      const dates = formData.dates || charter.dates;
      const quoteDate = charter.quoteDate || dayjs()?.format(DATE_STRING);
      const paymentDue = charter.paymentDue || dayjs(quoteDate)?.add(2, 'w')?.format(DATE_STRING);

      const updatedBooking = {
        ...charter,
        ...formData,
        dates: charter.charterType === 'standard' ? dates?.map(date => date ? dayjs(date).format(DATE_STRING) : null) : [],
        startDate: startDate ? dayjs(startDate).format(DATE_STRING) : null,
        endDate: endDate ? dayjs(endDate).format(DATE_STRING) : null,
        quoteDate: quoteDate,
        paymentDue: paymentDue
      };
      updatedBooking.scheduleId = charter.charterType === 'recurring' ? updatedBooking.scheduleId : null;

      const updatedCharter = new Charter({ ...updatedBooking });

      // Generate quote if there are itinerary legs
      if (updatedCharter.itinerary?.length > 0) {
        await updatedCharter.generateQuote({
          operator,
          allVehicleTypes,
          allCharterSchedules,
        });
      }
      const response = await charterModelData.save(updatedCharter);
      if (response) {
        setCharter(response);
        setMode({ edit: false });
      }
      if(charterId === '_new_' || charterId === '_') {
        // navigate to the new booking
        history.push(`/bookings/${updatedCharter.charterId}`);
     }
    } catch (error) {
      console.error("Error saving booking:", error);
    }
  };

  const setBookingAndSave = useCallback(async (propName, value) => {
    setBookingWith(propName, value);
    const updated = new Charter({ ...charter });
    if (Array.isArray(propName)) {
      propName.forEach((p, idx) => {
        updated[p] = value[idx];
      });
    } else {
      updated[propName] = value;
    }
    await charterModelData.save(updated);
  }, [setBookingWith, charter]);

  const createPDF = useCallback(async () => {
    const tempContainer = document.createElement('div');
    tempContainer.style.width = '210mm';
    tempContainer.style.minHeight = '297mm';
    tempContainer.style.background = 'white';
    tempContainer.style.boxSizing = 'border-box';
    tempContainer.style.fontFamily = 'Arial, sans-serif';
    document.body.appendChild(tempContainer);

    let Template;
    let title;
    switch (pdfType) {
        case 'confirmation':
            Template = ConfirmationTemplate;
            title = 'Booking Confirmation';
            break;
        case 'invoice':
            Template = InvoiceTemplate;
            title = 'Tax Invoice';
            break;
        case 'receipt':
            Template = ReceiptTemplate;
            title = 'Payment Receipt';
            break;
        default:
            Template = QuoteTemplate;
            title = 'Charter Quote';
    }

    // Render the appropriate template
    ReactDOM.render(
      <Template
        charter={charter}
        allCharterSchedules={allCharterSchedules}
        operator={operator}
        bookings={true}
      />,
      tempContainer
    );

    // Wait for the next tick to ensure the template is rendered
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // Remove all images from the container to avoid CORS issues
      const images = tempContainer.getElementsByTagName('img');
      Array.from(images).forEach(img => {
        if (img.id !== 'operator-logo') {
          img.remove();
        }
      });

      const canvas = await html2canvas(tempContainer, {
            scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
            windowWidth: 793.92,
            windowHeight: 1122.52,
      });

      ReactDOM.unmountComponentAtNode(tempContainer);
      document.body.removeChild(tempContainer);

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4', true);

      const pageWidth = 210;
      const pageHeight = 297;

      const scale = Math.min(pageWidth / (canvas.width / 3.78), pageHeight / (canvas.height / 3.78));
      const scaledWidth = (canvas.width / 3.78) * scale;
      const scaledHeight = (canvas.height / 3.78) * scale;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(
        imgData,
        'PNG',
        x,
        y,
        scaledWidth,
        scaledHeight
      );

      const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], `${title}.pdf`, { type: 'application/pdf' });

      setMailData((mail) => ({
        ...mail,
        subject: `${title} - ${charter.name || ''}`,
        attachments: [pdfFile],
      }));
      return pdfFile;
    } catch (error) {
      console.error('Error generating PDF:', error);
      ReactDOM.unmountComponentAtNode(tempContainer);
      document.body.removeChild(tempContainer);
      throw error;
    }
}, [charter, pdfType, operator, allCharterSchedules, setMailData]);

  const onDownloadClicked = useCallback(async () => [await createPDF()], [createPDF]);

  const handlePrint = async () => {
    try {
      const pdfFile = await createPDF();
      const pdfUrl = URL.createObjectURL(pdfFile);
      const printWindow = window.open(pdfUrl, '_blank');
      if(printWindow){
          printWindow.onload = function() {
              printWindow.print();
              setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          };
      }
  } catch (error) {
      console.error('Error printing PDF:', error);
      window.print();
  }
}

  useEffect(() => {
    let to = [];
    let cc = [];
    if (charter.customer?.email) {
      to = [charter.customer.email];
    }

    if (operator?.operatorEmail) {
      cc.push(operator.operatorEmail);
    }

    charter.customer?.additionalContacts?.map(contact => {
      if (contact.email) {
        cc.push(contact.email);
      }
    })

    setMailData((mail) => ({
      ...mail,
      to,
      cc,
      bcc: [],
      onMailComplete: () => {
        const _charter = new Charter({ ...charter });
        const updatedQuoteStatus = charter.quoteStatus === QUOTE_STATUS.draft.value ? QUOTE_STATUS.sent.value : charter.quoteStatus === QUOTE_STATUS.invoiceDraft.value ? QUOTE_STATUS.awaitingPayment.value : charter.quoteStatus;
        setBookingWith('quoteStatus', updatedQuoteStatus);
        _charter.markAsSent();
        charterModelData.save(_charter);
      },
      onMailOpen: () => {
        createPDF();
      },
    }));
  }, [charter, operator, setBookingWith, createPDF]);

  const showShiftbatCard = useMemo(() => {
    const validStatuses = ['scheduled', 'accepted', 'allocated', 'completed', 'cancelled'];
    return validStatuses.includes(charter.status) && charter.itinerary?.length > 0;
  }, [charter.status, charter.itinerary]);

  return (
    <div className="charter-details w-secondary-menu">
      {/* <h2 className="print-title">{startCase(charter.name)}</h2> */}
      {charter ? (
        <>
          <SaveMenu
            save={handleSave}
            editMode={mode.edit}
            id={charter.charterId}
            setEditMode={(edit) => setMode({ edit })}
            controls={controls}
            updated={controls.canUndo}
            modelInstance={charter}
            initialInstanceRef={initialBookingRef}
            listUrl="/bookings"
            modelService={charterModelData}
            closeView={true}
            //showPrint={true}
            //showSend={true}
            showMessageFieldInEmail={false}
            mailData={mailData}
            //onDownloadClicked={onDownloadClicked}
            //createPrintPDF={createPDF}
          />
          <Row gutter={[20, 20]} className="w-100 hide-print">
            <Col xs={24} lg={24}>
              <Row gutter={[20, 20]}>
                <Col xs={24} lg={12}>
                  <BookingDetailsCard mode={mode} charter={charter} setBookingWith={setBookingWith} form={form} operator={operator} />
                </Col>
                <Col xs={24} lg={12}>
                  <CharterLegsCard setMode={setMode} mode={mode} charter={charter} setBookingWith={setBookingWith} setCharter={setCharter} />
                </Col>
                <Col xs={24} lg={24}>
                  <BookingBilling mode={mode} charter={charter} setBookingWith={setBookingWith} setBookingAndSave={setBookingAndSave} mail={mailData} setPdfType={setPdfType} handlePrint={handlePrint} onDownloadClicked={onDownloadClicked}/>
                </Col>
                {showShiftbatCard &&
                  <Col xs={24} lg={12}>
                    <ShiftbatCard setMode={setMode} mode={mode} charter={charter} setBookingWith={setBookingWith} setCharter={setCharter} />
                  </Col>}
                {charter.quoteHistory?.length > 0 &&
                <Col xs={24} lg={12}>
                  <Card title="History" bordered={false} className="card-main booking-details">
                    <div className="scrollable-container mt-2"><DocumentHistory charter={charter} /></div>
                  </Card>
                </Col>}
              </Row>
            </Col>
          </Row>
          <div className="print-only-area w-100">
            <QuoteTemplate
              charter={charter}
              allCharterSchedules={allCharterSchedules}
              operator={operator}
              bookings={true}
            />
          </div>
        </>
      ) : (
        <LoadMessage message="Loading Booking Details..." />
      )}
    </div>
  );
}

export default BookingDetails;
